import { useState, useEffect, useRef } from "react"
import { gsap } from "gsap"

function shuffle(a) {
  for (let i = a.length - 1; i > 0; i--) {
    const j = (Math.random() * (i + 1)) | 0
    ;[a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

function anchorCell(anchor, cols, rows) {
  switch (anchor) {
    case "top-left":
      return { x: 0, y: 0 }
    case "top-right":
      return { x: cols - 1, y: 0 }
    case "bottom-right":
      return { x: cols - 1, y: rows - 1 }
    case "bottom-left":
    default:
      return { x: 0, y: rows - 1 }
  }
}

// exactly `fill` cells, with >=1 in each row and >=1 in each column,
// each tagged with its distance from the bloom anchor and a color index.
function generateCells({ cols, rows, fill, anchor }) {
  const chosen = []
  const usedRows = new Set()
  const usedCols = new Set()

  // Create all possible cells
  const allCells = []
  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < cols; x++) {
      allCells.push({ x, y })
    }
  }
  shuffle(allCells)

  // Phase 1: Pick cells that cover uncovered rows/cols
  for (const cell of allCells) {
    if (chosen.length >= fill) break

    const needsRow = !usedRows.has(cell.y)
    const needsCol = !usedCols.has(cell.x)

    // Prioritize cells that cover both an uncovered row AND column
    if (needsRow && needsCol) {
      chosen.push(cell)
      usedRows.add(cell.y)
      usedCols.add(cell.x)
    }
  }

  // Phase 2: Cover any remaining uncovered rows
  for (const cell of allCells) {
    if (chosen.length >= fill) break
    if (!usedRows.has(cell.y)) {
      chosen.push(cell)
      usedRows.add(cell.y)
      usedCols.add(cell.x)
    }
  }

  // Phase 3: Cover any remaining uncovered columns
  for (const cell of allCells) {
    if (chosen.length >= fill) break
    if (!usedCols.has(cell.x)) {
      chosen.push(cell)
      usedRows.add(cell.y)
      usedCols.add(cell.x)
    }
  }

  // Phase 4: Fill remaining slots randomly
  for (const cell of allCells) {
    if (chosen.length >= fill) break
    if (!chosen.includes(cell)) {
      chosen.push(cell)
    }
  }

  const a = anchorCell(anchor, cols, rows)
  return chosen.map((c) => ({
    ...c,
    d: Math.hypot(c.x - a.x, c.y - a.y),
  }))
}

export default function PixelComponent({
  anchor = "bottom-left",
  cols = 4,
  rows = 3,
  fill = 6,
  pixelSize = 50,
  stagger = 0.45,
  duration = 0.42,
  ease = "back.out(1.8)",
  triggerAt = 0.5,
  triggerAfterDelay = false,
  className,
}) {
  const ref = useRef(null)
  const [cells, setCells] = useState([])

  if (window.matchMedia("(max-width: 1200px)").matches) {
    pixelSize = 30
  }

  if (window.matchMedia("(max-width: 860px)").matches) {
    pixelSize = 15
  }

  useEffect(() => {
    setCells(generateCells({ cols, rows, fill, anchor }))
  }, [cols, rows, fill, anchor])

  useEffect(() => {
    if (!cells.length || !ref.current) return
    const el = ref.current
    const nodes = gsap.utils.toArray(el.children)
    const maxD = Math.max(...cells.map((c) => c.d)) || 1

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      gsap.set(nodes, { opacity: 1, scale: 1 })
      return
    }

    let tween
    if (triggerAfterDelay) {
      tween = gsap.to(nodes, {
        opacity: 1,
        scale: 1,
        duration,
        ease,
        delay: triggerAfterDelay,
        stagger: (i, t) => (parseFloat(t.dataset.d) / maxD) * stagger,
      })
      return
    }

    const io = new IntersectionObserver(
      ([entry], obs) => {
        if (!entry.isIntersecting) return
        tween = gsap.to(nodes, {
          opacity: 1,
          scale: 1,
          duration,
          ease,
          stagger: (i, t) => (parseFloat(t.dataset.d) / maxD) * stagger,
        })
        obs.unobserve(el) // once
      },
      { rootMargin: `0px 0px -${(1 - triggerAt) * 100}% 0px`, threshold: 0 },
    )
    io.observe(el)

    return () => {
      io.disconnect()
      tween?.kill()
    }
  }, [cells, duration, ease, stagger, triggerAt, triggerAfterDelay])

  return (
    <div
      ref={ref}
      className={className}
      aria-hidden="true"
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${cols}, ${pixelSize}px)`,
        gridTemplateRows: `repeat(${rows}, ${pixelSize}px)`,
      }}
    >
      {cells.map((c) => (
        <span
          key={`${c.x}-${c.y}`}
          data-d={c.d}
          style={{
            gridColumn: c.x + 1,
            gridRow: c.y + 1,
            background: "var(--pinkColor)",
            outline: "1px solid var(--pinkColor)",
            opacity: 0,
            transform: "scale(0.35)",
            willChange: "opacity, transform",
          }}
        />
      ))}
    </div>
  )
}
